/* eslint-disable complexity */
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import useDeviceSubtype from 'hooks/useDeviceSubtype';
import useDeviceType from 'hooks/useDeviceType';
import useGenericDevice from 'hooks/useGenericDevice';
import useProfile from 'hooks/useProfile';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import type { FC } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import ActivityLabel from 'components/ActivityLabel';
import DeviceIconComponent from 'components/DeviceIconComponent';
import ErrorText from 'components/ErrorText';
import Modal from 'components/Modal';
import FilledTextField from 'components/MuiCustom/FilledTextField';

import { useAppDispatch, useAppSelector } from 'redux-store';
import {
  groupConfigDevices,
  removePendingDeviceConfig,
} from 'redux-store/slices/groupConfigDevices';
import type { DeviceActivityStatus } from 'redux-store/slices/ui/types';
import getAllDevicesThunk from 'redux-store/thunks/devices/getAllDevicesThunk';
import deleteDeviceConfigQueueThunk from 'redux-store/thunks/groupConfigDevices/deleteDeviceConfigQueueThunk';
import reapplyProtectedConfigurationThunk from 'redux-store/thunks/groupConfigDevices/reapplyProtectedConfigurationThunk';

interface DeviceGroupConfigModalProps extends DefaultPopupProps {
  deviceSerialNumber?: string;
  status: DeviceActivityStatus;
}

const TroubleshootingDevicesModal: FC<DeviceGroupConfigModalProps> = ({
  open,
  onClose,
  deviceSerialNumber,
  status,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const profile = useProfile();

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [technicianPassword, setTechnicianPassword] = useState('');
  const [technicianPasswordError, setTechnicianPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const friendlyName = useGenericDevice(
    deviceSerialNumber,
    state => state?.fna,
  );

  const deviceType = useDeviceType(deviceSerialNumber);

  const deviceSubtype = useDeviceSubtype(deviceSerialNumber);

  const groupConfigDevicesResponse = useAppSelector(state =>
    state.groupConfigDevices.devices.find(
      item => item.serial === deviceSerialNumber,
    ),
  );

  const selectedDevice = useAppSelector(state =>
    state.devices.items.find(device => device.serial === deviceSerialNumber),
  );

  const handleDeletePendingDeviceConfigQueue = useCallback(async () => {
    if (!selectedDevice?.id) return;

    setLoading(true);

    try {
      const result = await dispatch(
        deleteDeviceConfigQueueThunk(selectedDevice.id),
      ).unwrap();

      if (result.success) {
        dispatch(removePendingDeviceConfig(selectedDevice.serial));

        setTimeout(() => {
          if (profile?.id) {
            dispatch(getAllDevicesThunk({ userId: profile.id }));
          }
          setLoading(false);
          setError('');
          onClose();
        }, 1250);
      } else {
        setLoading(false);
        setError(
          t('common:pages.device_setup.troubleshootingModal.unable_to_remove'),
        );
      }
    } catch (err) {
      setLoading(false);
      setError((err as Error).message || 'An unexpected error occurred.');

      Sentry.captureException(err, {
        extra: { deviceId: selectedDevice.id },
        tags: { page: 'TroubleshootingDevicesModal' },
      });
    }
  }, [
    selectedDevice?.id,
    selectedDevice?.serial,
    dispatch,
    profile?.id,
    onClose,
    t,
  ]);

  const handleVerifyTechnicianPassword = useCallback(async () => {
    if (!selectedDevice?.id) return;

    setError(null);
    setTechnicianPasswordError('');

    if (!technicianPassword || technicianPassword.length < 8) {
      setTechnicianPasswordError(
        t('common:pages.settings.subpages.password.error.password_too_short'),
      );

      return;
    }

    setLoading(true);

    try {
      const result = await dispatch(
        reapplyProtectedConfigurationThunk({
          technicianPassword: { technicianPassword },
          deviceId: selectedDevice.id,
        }),
      ).unwrap();

      if (result.success) {
        dispatch(removePendingDeviceConfig(selectedDevice.serial));

        dispatch(
          groupConfigDevices([
            {
              serial: selectedDevice.serial,
              success: result.success,
              configurations: [],
            },
          ]),
        );

        setTimeout(() => {
          if (profile?.id) {
            dispatch(getAllDevicesThunk({ userId: profile.id }));
          }
          setLoading(false);
          setTechnicianPassword('');
          onClose();
        }, 1250);
      } else {
        setLoading(false);
        if ('error' in result) {
          setError(result.error as string);
        }

        return;
      }
    } catch (err) {
      setLoading(false);
      setError((err as Error).message || 'An unexpected error occurred.');
    }
  }, [selectedDevice, technicianPassword, t, dispatch, profile, onClose]);

  const deviceStatusInfo: Record<NonNullable<DeviceActivityStatus>, string> = {
    Pending:
      'common:pages.device_setup.troubleshootingModal.troubleshootingDescPending',
    Protected:
      'common:pages.device_setup.troubleshootingModal.troubleshootingDescProtected',
    Failed:
      'common:pages.device_setup.troubleshootingModal.troubleshootingDescFailed',
    Completed: '',
  };

  const onCloseModal = useCallback((): void => {
    setError(null);
    setTechnicianPassword('');
    setTechnicianPasswordError('');
    onClose();
  }, [onClose]);

  return (
    <Modal open={open} onClose={onCloseModal}>
      <Box mb={3} height="100%" display="flex" flexDirection="column">
        <Typography variant="h2" textAlign="center" mb={2}>
          {status === 'Protected'
            ? t(
                'common:pages.device_setup.troubleshootingModal.restrictedConfigTitle',
              )
            : t(
                'common:pages.device_setup.troubleshootingModal.troubleshootingTitle',
              )}
        </Typography>
        <Typography variant="body1" textAlign="center">
          {status && deviceStatusInfo[status]
            ? t(deviceStatusInfo[status])
            : ''}
        </Typography>
        <ErrorText mt={1} error={error} />
        <Box
          mt={4}
          display="flex"
          flexDirection="column"
          overflow="auto"
          flexGrow={0}
          flexShrink={1}
          flexBasis="auto"
          width="100%"
        >
          <List sx={{ borderRadius: 8 }}>
            <ListItem
              sx={{
                backgroundColor: theme.vars.palette.grey[100],
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              }}
              divider
            >
              <ListItemIcon>
                <DeviceIconComponent typ={deviceType} subtype={deviceSubtype} />
              </ListItemIcon>
              <ListItemText
                primary={
                  friendlyName ??
                  t('common:serial_number_big_with_value', {
                    value: deviceSerialNumber,
                  })
                }
                secondary={deviceSerialNumber}
              />
              {status ? <ActivityLabel status={status} /> : null}
            </ListItem>
            {status === 'Pending' || status === 'Protected' ? (
              <ListItemButton
                sx={{
                  backgroundColor: theme.vars.palette.grey[100],
                  borderBottomLeftRadius: 16,
                  borderBottomRightRadius: 16,
                }}
                disabled={isLoading}
                onClick={handleDeletePendingDeviceConfigQueue}
              >
                <ListItemText
                  primary={t(
                    'common:pages.device_setup.troubleshootingModal.remove_queue',
                  )}
                  primaryTypographyProps={{
                    style: {
                      color: isLoading
                        ? theme.vars.palette.grey[500]
                        : theme.vars.palette.error.main,
                      fontWeight: 600,
                    },
                  }}
                />
                <ListItemIcon
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <CancelOutlinedIcon color="error" />
                </ListItemIcon>
              </ListItemButton>
            ) : null}
            {status === 'Failed' ? (
              <Box>
                {groupConfigDevicesResponse?.configurations
                  .filter(setting => setting.error)
                  .map((setting, index, array) => (
                    <ListItem
                      key={setting.key}
                      divider={index < array.length - 1}
                      sx={{
                        backgroundColor: theme.vars.palette.grey[100],
                        ...(index === array.length - 1 && {
                          borderBottomLeftRadius: 16,
                          borderBottomRightRadius: 16,
                        }),
                      }}
                    >
                      <ListItemIcon>
                        <ErrorOutlineIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t(
                          `common:pages.device_setup.troubleshootingModal.${setting.key}`,
                        )}
                        secondary={setting.error}
                      />
                    </ListItem>
                  ))}
              </Box>
            ) : null}
          </List>
          {status === 'Protected' ? (
            <Box mt={4}>
              <FilledTextField
                label={t(
                  'common:pages.settings.subpages.password.technician_password',
                )}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={technicianPassword}
                onChange={e => setTechnicianPassword(e.target.value)}
                error={!!technicianPasswordError}
                helperText={technicianPasswordError}
                fullWidth
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                autoFocus
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 4 }}
                onClick={handleVerifyTechnicianPassword}
                disabled={
                  !technicianPassword ||
                  technicianPassword.length < 8 ||
                  isLoading
                }
              >
                {t('common:apply')}
              </Button>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Modal>
  );
};

export default TroubleshootingDevicesModal;
